import { router } from "@inertiajs/react";
import { Coins } from "lucide-react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Avatar } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Icon } from "@/components/ui/icon";
import { SheetFooter } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { PhoneHelpers } from "@/lib/phone";
import { type TransactionFormData, TYPES_NEEDING_DIRECT_POINTS } from "../transaction-form-schema";
import { TransactionSummary } from "../transaction-summary";
import type { UseCreateTransactionWizardReturn } from "./use-create-transaction-wizard";

interface StepConfirmationProps {
  wizard: UseCreateTransactionWizardReturn;
  onBack: () => void;
  onSuccess: () => void;
}

export function StepConfirmation({ wizard, onBack, onSuccess }: StepConfirmationProps) {
  const { t } = useTranslation();
  const form = useFormContext<TransactionFormData>();
  const formData = form.getValues();

  const { calculation, isCalculating, calculationError, createMutation, idempotencyKey, regenerateIdempotencyKey } = wizard;

  const selectedUser = formData.user;
  const transactionType = formData.transaction_type;
  const isPurchaseType = transactionType === "purchase";
  const isReversalType = transactionType === "reversal";
  const reversedTransaction = formData.reversed_transaction ?? null;

  const currentBalance = selectedUser?.loyalty_account?.current_points || 0;

  function getPointsWithSign(transactionType: string, pointsValue: number): number {
    if (
      !TYPES_NEEDING_DIRECT_POINTS.includes(
        transactionType as (typeof TYPES_NEEDING_DIRECT_POINTS)[number],
      )
    ) {
      return pointsValue;
    }
    const isEarning = ["bonus", "manual_adjustment", "refund", "signup_bonus"].includes(
      transactionType,
    );
    return isEarning ? Math.abs(pointsValue) : -Math.abs(pointsValue);
  }

  async function handleCreateTransaction() {
    if (!formData.user?.loyalty_account || !formData.station) return;

    const requiresCalculation =
      !isReversalType &&
      (formData.transaction_type === "purchase" || formData.transaction_type === "direct_spending");
    if (requiresCalculation && !calculation) return;

    try {
      const response = await createMutation.mutateAsync({
        loyalty_account_id: formData.user.loyalty_account.id,
        transaction_type: formData.transaction_type,
        purchase_value_cents: formData.purchase_value_cents,
        points: getPointsWithSign(formData.transaction_type, formData.points),
        station_id: formData.station.id.toString(),
        description: formData.description || undefined,
        reversed_transaction_id: formData.reversed_transaction_id ?? undefined,
        idempotencyKey,
      });

      regenerateIdempotencyKey();
      if (response.data.transaction) {
        wizard.setCreatedTransaction(response.data.transaction);
      }
      onSuccess();
      router.reload();
    } catch {
      // Keep same key for retries - error handled by mutation state
    }
  }

  const isSubmitting = createMutation.isPending;
  const error = createMutation.error?.message;

  return (
    <>
      <div className="space-y-6">
        {/* Customer Info */}
        {selectedUser && (
          <Card>
            <CardContent>
              <div className="flex items-center gap-3">
                <Avatar name={selectedUser.name} className="size-12 text-base" />
                <div className="flex-1 space-y-0.5">
                  <h2 className="text-base font-semibold text-foreground">{selectedUser.name}</h2>
                  <p className="text-xs text-muted-foreground">
                    {PhoneHelpers.format(selectedUser.phone_number)}
                  </p>
                </div>
                <div className="flex items-baseline gap-1.5">
                  <Coins className="size-4 text-muted-foreground" />
                  <span className="text-lg font-bold tabular-nums text-foreground">
                    {currentBalance.toLocaleString()}
                  </span>
                  <span className="text-xs text-muted-foreground">{t("common.pts")}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Reversal Summary */}
        {isReversalType && reversedTransaction && (
          <Card>
            <CardContent className="space-y-1">
              <p className="text-sm text-muted-foreground">{t("transactions.create.reversing")}</p>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold">
                    {t(`transaction_types.${reversedTransaction.transaction_type}`)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {reversedTransaction.code} · {new Date(reversedTransaction.created_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-muted-foreground">{t("transactions.create.reversal_points")}</p>
                  <p className="text-base font-bold tabular-nums">
                    -{Math.abs(reversedTransaction.points).toLocaleString()} pts
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Calculation Summary */}
        {!isReversalType && isCalculating && !calculation && (
          <Card>
            <CardContent className="space-y-3">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-4 w-2/3" />
            </CardContent>
          </Card>
        )}

        {!isReversalType && calculationError && (
          <Alert variant="error">
            <Icon name="exclamation-circle" />
            <AlertTitle>{t("transactions.create.calculation_error")}</AlertTitle>
            <AlertDescription>{calculationError.message}</AlertDescription>
          </Alert>
        )}

        {!isReversalType && calculation && (
          <TransactionSummary
            purchaseValue={calculation.purchase_value_formatted}
            pointsToRewards={calculation.raw_points_for_rewards}
            pointsToSpend={calculation.points_to_spend}
            pointsToEarn={calculation.points_to_earn}
            transactionType={transactionType}
            points={calculation.points}
            isPurchaseType={isPurchaseType}
            cashValue={
              calculation.cash_value_cents > 0 ? calculation.cash_value_formatted : undefined
            }
            coveredValue={
              calculation.covered_value_cents > 0 ? calculation.covered_value_formatted : undefined
            }
          />
        )}

        {/* Error Alert */}
        {error && (
          <Alert variant="error">
            <Icon name="exclamation-circle" />
            <AlertTitle>{t("transactions.create.error_happened")}</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
      </div>

      <SheetFooter>
        <Button type="button" variant="outline" onClick={onBack} disabled={isSubmitting}>
          {t("transactions.create.back")}
        </Button>
        <Button type="button" onClick={handleCreateTransaction} disabled={isSubmitting || isCalculating}>
          {isSubmitting ? t("transactions.create.creating") : t("transactions.create.create_transaction")}
        </Button>
      </SheetFooter>
    </>
  );
}
